import {
  BufferImageSource,
  Container,
  Graphics,
  Sprite,
  Texture,
} from "pixi.js";
import type { Point } from "./geometry";
import { COMBAT_VFX_LIMITS } from "./combat-vfx-presentation";

export interface CombatViewport {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface BurstView {
  container: Container;
  halo: Sprite;
  ring: Graphics;
  sparks: Sprite[];
  image: Sprite;
  startedAt: number;
  duration: number;
  size: number;
  kind: "hull" | "explosion";
}

export function combatPointVisible(
  point: Point,
  radius: number,
  viewport?: CombatViewport,
): boolean {
  return (
    !viewport ||
    (point.x + radius >= viewport.x &&
      point.x - radius <= viewport.x + viewport.width &&
      point.y + radius >= viewport.y &&
      point.y - radius <= viewport.y + viewport.height)
  );
}

function glowTexture(): Texture {
  const size = 64;
  const pixels = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const index = (y * size + x) * 4;
      const distance = Math.hypot(x - 31.5, y - 31.5) / 31.5;
      pixels[index] = pixels[index + 1] = pixels[index + 2] = 255;
      pixels[index + 3] = Math.round(Math.max(0, 1 - distance) ** 2 * 255);
    }
  return new Texture({
    source: new BufferImageSource({
      resource: pixels,
      width: size,
      height: size,
      format: "rgba8unorm",
      alphaMode: "no-premultiply-alpha",
      scaleMode: "linear",
    }),
  });
}

export class CombatVfx {
  readonly container = new Container({ label: "combat-bursts" });
  readonly trails = new Container({ label: "projectile-trails" });
  readonly heads = new Container({ label: "projectile-heads" });
  private readonly glow = glowTexture();
  private readonly impacts: BurstView[] = [];
  private readonly explosions: BurstView[] = [];
  private readonly trailSprites: Sprite[] = [];
  private trailCount = 0;
  private readonly headSprites: Sprite[] = [];
  private headCount = 0;
  private viewport: CombatViewport | undefined;

  constructor(private readonly frames: readonly Texture[]) {}

  impact(impact: Point & { radius: number }, now: number): void {
    if (!combatPointVisible(impact, impact.radius * 2, this.viewport)) return;
    const view = this.acquire(this.impacts, COMBAT_VFX_LIMITS.impacts, now);
    if (!view) return;
    this.start(view, impact, impact.radius * 2, "hull", now);
  }

  explode(point: Point, size: number, now: number): void {
    if (
      !this.frames[0] ||
      !combatPointVisible(point, size * 1.2, this.viewport)
    )
      return;
    const view = this.acquire(
      this.explosions,
      COMBAT_VFX_LIMITS.explosions,
      now,
    );
    if (!view) return;
    this.start(view, point, size, "explosion", now);
  }

  beginFrame(viewport?: CombatViewport): void {
    this.viewport = viewport;
    this.trailCount = 0;
    this.headCount = 0;
  }

  projectileHead(point: Point, tint: number, diameter: number): void {
    let sprite = this.headSprites[this.headCount];
    if (!sprite) {
      sprite = new Sprite({
        texture: this.glow,
        anchor: 0.5,
        blendMode: "add",
        label: "projectile-head",
      });
      this.headSprites.push(sprite);
      this.heads.addChild(sprite);
    }
    this.headCount++;
    sprite.visible = true;
    sprite.position.set(point.x, point.y);
    sprite.tint = tint;
    sprite.width = sprite.height = diameter;
    sprite.alpha = 1;
  }

  trail(point: Point, angle: number, length: number, tint: number): void {
    if (
      this.trailCount >= COMBAT_VFX_LIMITS.projectileTrails ||
      !combatPointVisible(point, length, this.viewport)
    )
      return;
    let sprite = this.trailSprites[this.trailCount];
    if (!sprite) {
      sprite = new Sprite({
        texture: this.glow,
        anchor: 0.5,
        blendMode: "add",
      });
      sprite.label = "projectile-trail";
      this.trailSprites.push(sprite);
      this.trails.addChild(sprite);
    }
    this.trailCount++;
    sprite.visible = true;
    sprite.position.set(
      point.x - Math.cos(angle) * length * 0.5,
      point.y - Math.sin(angle) * length * 0.5,
    );
    sprite.rotation = angle;
    sprite.width = length;
    sprite.height = 8;
    sprite.tint = tint;
    sprite.alpha = 0.42;
  }

  render(now: number, reducedMotion = false): void {
    while (
      this.headSprites.length >
      this.headCount + COMBAT_VFX_LIMITS.spareProjectiles
    )
      this.headSprites.pop()?.destroy();
    for (let index = this.headCount; index < this.headSprites.length; index++) {
      const sprite = this.headSprites[index];
      if (sprite) sprite.visible = false;
    }
    for (
      let index = reducedMotion ? 0 : this.trailCount;
      index < this.trailSprites.length;
      index++
    ) {
      const sprite = this.trailSprites[index];
      if (sprite) sprite.visible = false;
    }
    for (const views of [this.impacts, this.explosions])
      for (const view of views) {
        const age = Math.max(0, (now - view.startedAt) / view.duration);
        if (age >= 1) {
          view.container.visible = false;
          continue;
        }
        view.container.visible = combatPointVisible(
          view.container,
          view.size * 1.2,
          this.viewport,
        );
        if (!view.container.visible) continue;
        const explosion = view.kind === "explosion";
        const fade = (1 - age) ** 2;
        view.halo.alpha = fade * (explosion ? 0.9 : 0.7);
        view.halo.width = view.halo.height =
          view.size *
          (reducedMotion ? 1 : explosion ? 1.1 + age * 1.5 : 0.8 + age * 0.5);
        view.ring.visible = !reducedMotion;
        view.ring.alpha = fade * (view.kind === "hull" ? 0.2 : 0.65);
        view.ring.scale.set(view.size * (0.3 + age * (explosion ? 1 : 0.3)));
        view.image.visible = explosion;
        if (explosion) {
          const frame =
            this.frames[
              Math.min(
                this.frames.length - 1,
                Math.floor(age * this.frames.length),
              )
            ];
          if (frame) view.image.texture = frame;
          const textureSize = Math.max(
            view.image.texture.width,
            view.image.texture.height,
            1,
          );
          view.image.scale.set(
            (view.size / textureSize) * (reducedMotion ? 1 : 0.85 + age * 0.35),
          );
          view.image.alpha = Math.min(1, (1 - age) * 3);
        }
        for (let index = 0; index < view.sparks.length; index++) {
          const spark = view.sparks[index];
          if (!spark) continue;
          spark.visible = !reducedMotion;
          const angle = index * Math.PI * 0.5 + 0.35;
          const distance = view.size * age * 0.85;
          spark.position.set(
            Math.cos(angle) * distance,
            Math.sin(angle) * distance,
          );
          spark.rotation = angle;
          spark.width = (explosion ? 12 : 8) * (1 - age) + 1;
          spark.height = 1.5;
          spark.alpha = fade;
        }
      }
  }

  destroy(): void {
    this.container.destroy({ children: true });
    this.trails.destroy({ children: true });
    this.heads.destroy({ children: true });
    this.glow.destroy(true);
  }

  private acquire(
    views: BurstView[],
    limit: number,
    now: number,
  ): BurstView | undefined {
    const available = views.find(
      (view) => now - view.startedAt >= view.duration,
    );
    if (available) return available;
    if (views.length >= limit) return undefined;
    const container = new Container();
    const halo = new Sprite({
      texture: this.glow,
      anchor: 0.5,
      blendMode: "add",
    });
    const image = new Sprite({
      texture: this.frames[0] ?? Texture.WHITE,
      anchor: 0.5,
    });
    const ring = new Graphics()
      .circle(0, 0, 1)
      .stroke({ color: 0xffffff, width: 0.025 });
    ring.blendMode = "add";
    const sparks = Array.from(
      { length: 4 },
      () =>
        new Sprite({
          texture: Texture.WHITE,
          anchor: 0.5,
          blendMode: "add",
          tint: 0xffcf8a,
        }),
    );
    container.addChild(halo, ring, image, ...sparks);
    this.container.addChild(container);
    const view: BurstView = {
      container,
      halo,
      ring,
      image,
      sparks,
      startedAt: -Infinity,
      duration: 1,
      size: 1,
      kind: "hull",
    };
    views.push(view);
    return view;
  }

  private start(
    view: BurstView,
    point: Point,
    size: number,
    kind: BurstView["kind"],
    now: number,
  ): void {
    view.startedAt = now;
    view.duration = kind === "explosion" ? 650 : 220;
    view.size = size;
    view.kind = kind;
    view.container.label = `combat-${kind}`;
    view.container.position.set(point.x, point.y);
    view.container.visible = false;
    view.halo.tint = 0xffaa55;
    view.ring.tint = 0xffcb8c;
  }
}
